import { apiFreezeCard, apiGetCard, apiUnfreezeCard, cardUpdated } from '@shared-core/features/backend'
import { useAppDispatch } from '@shared-core/store'
import { useEffect, useRef, useState } from 'react'

function readStatus(response: Awaited<ReturnType<typeof apiGetCard>>): string | undefined {
  return response?.status === 'ok' ? response.data?.status : undefined
}

export function useCardFreeze(cardId: string | undefined, status: string | undefined) {
  const dispatch = useAppDispatch()
  const [isRequesting, setIsRequesting] = useState(false)
  const [error, setError] = useState(false)
  const busyRef = useRef(false)
  const generationRef = useRef(0)
  const isTransitioning = status === 'freezing' || status === 'unfreezing'

  useEffect(() => {
    setIsRequesting(false)
    setError(false)
    busyRef.current = false
    return () => {
      generationRef.current += 1
    }
  }, [cardId])

  // Сервер завершает заморозку и разморозку асинхронно.
  useEffect(() => {
    if (!cardId || !isTransitioning) return
    let active = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const poll = async () => {
      try {
        const response = await apiGetCard({ routeParams: { id: cardId } })

        if (!active) return
        const nextStatus = readStatus(response)

        if (nextStatus) {
          dispatch(cardUpdated({ id: cardId, status: nextStatus }))
          setError(false)

          if (nextStatus !== 'freezing' && nextStatus !== 'unfreezing') return
        } else setError(true)
      } catch {
        if (active) setError(true)
      }

      if (active) timer = setTimeout(() => void poll(), 2000)
    }
    void poll()
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [cardId, dispatch, isTransitioning])

  const confirm = async () => {
    if (!cardId || busyRef.current || isTransitioning || (status !== 'active' && status !== 'frozen')) return false
    const generation = generationRef.current
    busyRef.current = true
    setIsRequesting(true)
    setError(false)
    try {
      const request = status === 'frozen' ? apiUnfreezeCard : apiFreezeCard
      const response = await request({ routeParams: { id: cardId } })

      if (generation !== generationRef.current) return false
      const nextStatus = readStatus(response)
      const expected = status === 'frozen' ? ['unfreezing', 'active'] : ['freezing', 'frozen']

      if (!nextStatus || !expected.includes(nextStatus)) {
        setError(true)
        return false
      }
      dispatch(cardUpdated({ id: cardId, status: nextStatus }))
      return true
    } catch {
      if (generation === generationRef.current) setError(true)
      return false
    } finally {
      if (generation === generationRef.current) {
        busyRef.current = false
        setIsRequesting(false)
      }
    }
  }

  return { confirm, error, isLoading: isRequesting || isTransitioning }
}
